import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Modal, Field } from "@/pages/ClientsPage";
import { useAuth } from "@/lib/auth";

const empty = { client_id: "", name: "", address: "", city: "", state: "", responsible: "", schedule: "", status: "active", notes: "" };

export default function LocationsPage() {
  const { user } = useAuth();
  const readOnly = user?.role === "cliente";
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = filterClient ? { params: { client_id: filterClient } } : {};
    const [{ data: locs }, { data: cls }] = await Promise.all([
      api.get("/locations", params),
      api.get("/clients"),
    ]);
    setItems(locs); setClients(cls); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterClient]);

  const clientMap = useMemo(() => Object.fromEntries(clients.map(c => [c.id, c.name])), [clients]);

  const openNew = () => { setEditing(null); setForm({ ...empty, client_id: filterClient || (clients[0]?.id || "") }); setErr(""); setModalOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setErr(""); setModalOpen(true); };
  const close = () => setModalOpen(false);

  const save = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      if (!form.client_id) throw { response: { data: { detail: "Selecciona un cliente" } } };
      if (editing) await api.put(`/locations/${editing.id}`, form);
      else await api.post("/locations", form);
      close(); await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar sucursal?")) return;
    await api.delete(`/locations/${id}`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="lx-overline">Catálogo</div>
          <h1 className="lx-section-title mt-1">{readOnly ? "Mis sucursales" : "Sucursales"}</h1>
          <p className="text-lx-muted text-sm mt-1">{readOnly ? "Sucursales asociadas a tu cuenta." : "Ubicaciones físicas donde se prestan los servicios."}</p>
        </div>
        <div className="flex gap-2 items-end">
          {!readOnly && (
            <div>
              <label className="lx-label">Filtrar por cliente</label>
              <select className="lx-input" value={filterClient} onChange={(e) => setFilterClient(e.target.value)} data-testid="filter-location-client">
                <option value="">Todos</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {!readOnly && (
            <button className="lx-btn lx-btn-primary h-[42px]" onClick={openNew} data-testid="new-location-button">
              <Plus size={16}/> Nueva sucursal
            </button>
          )}
        </div>
      </div>

      <div className="lx-card p-0 overflow-hidden">
        {loading ? <div className="p-6 text-lx-muted">Cargando…</div> : items.length === 0 ? (
          <div className="p-10 text-center text-lx-muted">
            <p>{readOnly ? "Aún no hay sucursales asignadas a tu cuenta." : `No hay sucursales ${filterClient ? "para este cliente" : "registradas"}.`}</p>
            {!readOnly && <button className="lx-btn lx-btn-primary mt-4" onClick={openNew}>Crear la primera</button>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lx-table">
              <thead>
                <tr><th>Sucursal</th><th>Cliente</th><th>Ciudad</th><th>Responsable</th><th>Estatus</th>{!readOnly && <th className="text-right">Acciones</th>}</tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr key={l.id} data-testid={`location-row-${l.id}`}>
                    <td>
                      <div className="font-medium">{l.name}</div>
                      {l.address && <div className="text-xs text-lx-muted">{l.address}</div>}
                    </td>
                    <td>{clientMap[l.client_id] || "—"}</td>
                    <td>{l.city || "—"}</td>
                    <td>{l.responsible || "—"}</td>
                    <td>
                      <span className={`lx-badge ${l.status === "active" ? "lx-badge-success" : "lx-badge-muted"}`}>
                        {l.status === "active" ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    {!readOnly && (
                      <td className="text-right whitespace-nowrap">
                        <button className="lx-btn lx-btn-ghost" onClick={() => openEdit(l)} data-testid={`edit-location-${l.id}`}><Pencil size={14}/></button>
                        <button className="lx-btn lx-btn-ghost" onClick={() => remove(l.id)} data-testid={`delete-location-${l.id}`}><Trash2 size={14}/></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <Modal title={editing ? "Editar sucursal" : "Nueva sucursal"} onClose={close}>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="lx-label">Cliente <span className="text-lx-error">*</span></label>
              <select className="lx-input" value={form.client_id} onChange={(e)=>setForm({...form, client_id: e.target.value})} required data-testid="location-client-select">
                <option value="">— Seleccionar —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <Field label="Nombre de la sucursal" required value={form.name} onChange={(v)=>setForm({...form, name: v})} testid="location-name"/>
            <Field label="Dirección" value={form.address} onChange={(v)=>setForm({...form, address: v})} testid="location-address"/>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Ciudad" value={form.city} onChange={(v)=>setForm({...form, city: v})} testid="location-city"/>
              <Field label="Estado" value={form.state} onChange={(v)=>setForm({...form, state: v})} testid="location-state"/>
              <Field label="Responsable" value={form.responsible} onChange={(v)=>setForm({...form, responsible: v})} testid="location-responsible"/>
              <Field label="Horario" placeholder="Ej. L-V 8:00-18:00" value={form.schedule} onChange={(v)=>setForm({...form, schedule: v})} testid="location-schedule"/>
            </div>
            <div>
              <label className="lx-label">Estatus</label>
              <select className="lx-input" value={form.status} onChange={(e)=>setForm({...form, status: e.target.value})} data-testid="location-status">
                <option value="active">Activo</option>
                <option value="inactive">Inactivo</option>
              </select>
            </div>
            {err && <div className="text-sm rounded-lg px-3 py-2 bg-red-50 text-red-700 border border-red-200">{err}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="lx-btn lx-btn-secondary" onClick={close}>Cancelar</button>
              <button type="submit" className="lx-btn lx-btn-primary" disabled={saving} data-testid="save-location-button">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
